import { useCallback, useRef, useState } from "react";
import { Element } from "slate";
import { v4 as uuidv4 } from 'uuid';
import moment from "moment";
import { IChatMessage, isIntegration } from "../../../types";
import { addOneToast, clearActiveMessageReply, selectCurrentUserId, setActiveChunk } from "../../../redux/slices";
import { failOptimisticMessage, insertOptimisticMessage, reconcileOptimisticMessage } from "../../../redux/slices/messageSlice/thunks";
import { useTypedDispatch, useTypedSelector } from "../../../redux/hooks";
import { slateToMarkdown } from './utils/slateToMarkdown';
import { useConversationContext } from "../../../context/ChatContext/contextConvo";
import { nebulaChatApi, PendingFileInfo, SendMessageRequest } from "../../../redux/services/nebulaChat";

export const useSendMessage = ({
    resetEditor
}: {
    resetEditor: () => void;
}) => {
    const { conversation, files } = useConversationContext();

    const messageReply = useTypedSelector(state => state.chat.messageReplies[conversation.uuid]);
    const userUuid = useTypedSelector(selectCurrentUserId);

    const dispatch = useTypedDispatch();


    const [sendMessageAPI] = nebulaChatApi.endpoints.sendMessageN.useMutation();
    const [sendFile] = nebulaChatApi.endpoints.uploadFileN.useMutation();

    const isSendingRef = useRef(false);

    const sendMessage = async (
        value: Element[],
        event?: any,
    ) => {
        if (event) event.preventDefault();

        if (isSendingRef.current) {
            console.warn("Tried to send message when already sending");
            return;
        }

        const attachedFiles = files;

        const content: string = slateToMarkdown(value).trim();

        // Validation is synchronous, so it runs before the lock — nothing can race in.
        const isSendDisabled = isIntegration(conversation) && (content.length > 750 || content.length < 1);

        if (isSendDisabled) {
            console.warn("Tried to send message but send disabled");
            return;
        }

        if (content.length < 1 && attachedFiles.length < 1) {
            console.warn("Tried to send message with no content and no files");
            return;
        }

        const totalFileSize = attachedFiles.reduce((sum, f) => sum + f.size, 0);
        if (totalFileSize > 40 * 1024 * 1024) {
            dispatch(addOneToast({
                content: "Total file size must be under 40 MB",
                title: "Files too large",
                type: "error"
            }));
            return;
        }

        const reply_to = messageReply?.message?.uuid;

        const request: SendMessageRequest = {
            to: conversation.uuid,
            to_type: conversation.type,
            content,
            reply_to,
            files: attachedFiles && attachedFiles.length > 0 ?
                attachedFiles.map(f => ({
                    name: f.name,
                    content_type: f.type,
                    size: f.size,
                })) : undefined
        };

        dispatch(setActiveChunk({
            chat_uuid: conversation.uuid,
            active_chunk: 'initial',
        }));

        if (reply_to) {
            dispatch(clearActiveMessageReply(conversation.uuid));
        }

        isSendingRef.current = true;

        const tempUuid = `temp-${uuidv4()}`;
        const now = moment().format();

        const optimisticMessage: IChatMessage = {
            uuid: tempUuid,
            from: userUuid,
            from_type: 'user',
            to: conversation.uuid,
            to_type: conversation.type as IChatMessage['to_type'],
            time: now,
            updated_at: now,
            content,
            reply_to,
            state: 'sending',
            has_thread: false,
            files: attachedFiles.map((f, index) => ({
                uuid: `${tempUuid}-file-${index}`,
                name: f.name,
                content_type: f.type,
                size: f.size,
                shared_by: userUuid,
                shared_at: now,
                pending: true,
                content: "",
                index,
            })),
        };

        dispatch(insertOptimisticMessage(optimisticMessage));

        // Clear the input immediately — the message now lives in the view, not the editor.
        resetEditor();

        let pending_files: PendingFileInfo[] | null | undefined = null;

        try {
            const sent = await sendMessageAPI(request).unwrap();
            pending_files = sent?.pending_files;
            dispatch(reconcileOptimisticMessage({
                tempUuid,
                chatUuid: conversation.uuid,
                message: sent.message,
            }));
        } catch (ex) {
            dispatch(failOptimisticMessage({ tempUuid, chatUuid: conversation.uuid }));
            console.error("Failed to send chat message", ex);
            return;
        } finally {
            // Released before uploads: the message is sent and the editor is clear, so
            // the user can fire the next message while these files finish uploading.
            isSendingRef.current = false;
        }

        if (pending_files && attachedFiles && pending_files.length > 0) {
            if (pending_files.length !== attachedFiles.length) {
                dispatch(addOneToast({
                    content: "Unable to send chat files at this time",
                    title: "Invalid chat files",
                    type: "error"
                }))
                console.error("Chat files not same length as pend", { pending_files, file_len: attachedFiles.length });
                return;
            }

            for (let i = 0; i < pending_files.length; i += 1) {
                const pending = pending_files[i];
                const local = attachedFiles[i];
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await sendFile({
                        file_uuid: pending.uuid,
                        // eslint-disable-next-line no-await-in-loop
                        data: await local.arrayBuffer(),
                    }).unwrap();
                } catch (ex) {
                    dispatch(addOneToast({
                        content: `Unable to send file "${local.name}" at this time`,
                        title: "Failed to send file",
                        type: "error"
                    }))
                    console.error("failed to send file", { pending, ex });
                }
            }
        }
    };

    return { send: sendMessage };
}

export const useUserTyping = () => {
    const { conversation } = useConversationContext()
    const userUuid = useTypedSelector(selectCurrentUserId)

    const [userStartedTyping, setUserStartedTyping] = useState(false);

    const [sendTypingStatus] = nebulaChatApi.endpoints.sendTypingStatusN.useMutation();

    const stopTypingTimer = useRef<ReturnType<typeof setTimeout>>();

    const sendUserStopTyping = useCallback(() => {
        if (stopTypingTimer.current) {
            clearTimeout(stopTypingTimer.current);
        }

        stopTypingTimer.current = setTimeout(() => {
            sendTypingStatus({
                user: userUuid,
                to: conversation.uuid,
                to_type: conversation.type,
                is_typing: false
            });
            setUserStartedTyping(false);
        }, 3000);
    }, [conversation.uuid, userUuid]);

    const handleTypingStatus = useCallback((charLength: number) => {
        if (charLength > 0 && !userStartedTyping) {
            setUserStartedTyping(true);
            sendTypingStatus({
                user: userUuid,
                to: conversation.uuid,
                to_type: conversation.type,
                is_typing: true
            })
        }
        sendUserStopTyping();
    }, [userStartedTyping]);

    return handleTypingStatus;
}
